import { STATES } from "../data/states";
import { DMAS } from "../data/dmas";

const ALL_GEOS = [...STATES, ...DMAS];

// Shared character class for list separators, so isGeoList's detection and
// resolveGeoList's split always agree on what counts as a separator.
const GEO_LIST_SEPARATOR_CLASS = "[,;\\t\\n]";
const GEO_LIST_SEPARATOR_RE = new RegExp(GEO_LIST_SEPARATOR_CLASS);

// Hard separators are never part of a geo name, unlike commas (e.g. "Albany, Ga"). Splitting
// on these first, before any comma-based rejoin, keeps a rejoin from ever crossing a column
// boundary in a pasted spreadsheet ("Albany" / "Ga" on separate lines must not become the
// "Albany, Ga" DMA).
const HARD_SEPARATOR_SPLIT_RE = /[;\t\r\n]+/;

// The widest comma-joined geo name (e.g. "Albany, Ga" has 2 parts) bounds how many consecutive
// tokens the rejoin loop ever needs to try, instead of scanning up to the full token count.
const MAX_GEO_NAME_PARTS = ALL_GEOS.reduce((max, g) => Math.max(max, g.name.split(",").length), 1);

// Precomputed lookup from lowercased name/key to its geo(s), so an exact match is a single Map
// lookup instead of a linear scan over ALL_GEOS per candidate.
const GEO_LOOKUP_MAP = new Map();
for (const g of ALL_GEOS) {
  const lookupKeys = new Set([g.name.toLowerCase(), g.key.toLowerCase()]);
  for (const lookupKey of lookupKeys) {
    if (!GEO_LOOKUP_MAP.has(lookupKey)) GEO_LOOKUP_MAP.set(lookupKey, []);
    GEO_LOOKUP_MAP.get(lookupKey).push(g);
  }
}

// Nielsen DMA code -> market name, for the DMA codes stored on reporting rows (DEV-2815).
const DMA_NAME_BY_CODE = new Map(DMAS.map(d => [d.key, d.name]));

// Market name for a stored DMA code; an unknown code is shown as is, a missing one is null.
export function dmaName(code) {
  const key = code == null ? "" : String(code).trim();
  if (!key) return null;
  return DMA_NAME_BY_CODE.get(key) ?? key;
}

// "city, region, country · DMA: <name>"; any part may be absent. `order` names the geo
// fields to join, for screens that show them in another order.
export function formatLocation(row = {}, order = ["city", "region", "country"]) {
  const location = order.map((field) => row[field]).filter(Boolean).join(", ");
  const market = dmaName(row.dma);
  if (!market) return location;
  return location ? `${location} \u00B7 DMA: ${market}` : `DMA: ${market}`;
}

// True when the text should be resolved as a pasted/typed list rather than searched as a
// single term. A list separator alone isn't enough: some geo names (e.g. "Albany, Ga")
// contain a comma, so typing or partially typing one of those must keep the normal
// substring search instead of jumping into list mode. Only enter list mode when the whole
// trimmed text has a separator AND doesn't itself match a real geo via searchGeos.
export function isGeoList(text, types = ["state"]) {
  return GEO_LIST_SEPARATOR_RE.test(text) && searchGeos(text, types).length === 0;
}

export function searchGeos(query, types = ["state"]) {
  if (!query.trim()) return [];
  const q = query.trim().toLowerCase();
  return ALL_GEOS.filter(
    g => types.includes(g.type) && (g.name.toLowerCase().includes(q) || g.key.toLowerCase().includes(q))
  ).map(g => ({ id: g.key, ...g }));
}

export function fetchGeosByKeys(keys, types = ["state"]) {
  if (!keys.length) return [];
  const keySet = new Set(keys.map(k => String(k)));
  return ALL_GEOS.filter(
    g => types.includes(g.type) && keySet.has(String(g.key))
  ).map(g => ({ id: g.key, ...g }));
}

// Splits a pasted/typed list on commas, semicolons, tabs and newlines and resolves each
// token to an exact geo match (by name or key, case-insensitive). Unlike searchGeos this
// is exact-match, not substring, since a CSV of full state names would otherwise collide
// with each other's substrings.
// Decision: "New York" state and "New York" DMA share a name. When a token matches both
// (types includes more than one of the matched types), we keep only the matches of the
// type that comes first in `types` — the caller's `types` order encodes its preferred
// intent (BudgetDrawer passes ["state", "dma"], so states win on a name collision).
// See geoService.test.js for the covered case.
// When includeZip is set, a token that isn't a geo but passes validateZipCode becomes a
// zip geo object in `matched` instead of `unmatched` (deduped like any other match).
// Exact-matches a run of consecutive tokens joined with ", " against the precomputed lookup.
function findExactGeos(joinedToken, types) {
  const candidates = GEO_LOOKUP_MAP.get(joinedToken.toLowerCase()) || [];
  return candidates.filter(g => types.includes(g.type));
}

export function resolveGeoList(text, types = ["state"], { includeZip = false } = {}) {
  // Split on hard separators first, so a rejoin (below) never crosses one of them: a comma is
  // the only separator that can also appear inside a geo name, so only commas get rejoined.
  const segments = (text || "").split(HARD_SEPARATOR_SPLIT_RE);

  const matched = [];
  const seenIds = new Set();
  const unmatched = [];

  for (const segment of segments) {
    const tokens = segment.split(",").map(t => t.trim()).filter(Boolean);

    // Greedily rejoin adjacent tokens within this segment: some geo names (e.g. "Albany, Ga")
    // contain a comma, so a naive split-then-match would break them into pieces that match
    // unrelated geos ("Ga" -> Georgia). Try the longest run first, bounded by the widest geo
    // name's part count, falling back to shorter runs, so a joined match always wins.
    let i = 0;
    while (i < tokens.length) {
      let found = [];
      let consumed = 1;
      const maxEnd = Math.min(tokens.length, i + MAX_GEO_NAME_PARTS);
      for (let end = maxEnd; end > i; end--) {
        const candidate = findExactGeos(tokens.slice(i, end).join(", "), types);
        if (candidate.length) {
          found = candidate;
          consumed = end - i;
          break;
        }
      }

      if (!found.length) {
        const token = tokens[i];
        if (includeZip && validateZipCode(token)) {
          const zipGeo = buildZipGeoObject(token);
          if (!seenIds.has(zipGeo.id)) {
            seenIds.add(zipGeo.id);
            matched.push(zipGeo);
          }
        } else {
          unmatched.push(token);
        }
        i += 1;
        continue;
      }

      const earliestType = types.find(t => found.some(g => g.type === t));
      const preferred = found.filter(g => g.type === earliestType);
      for (const g of preferred) {
        if (!seenIds.has(g.key)) {
          seenIds.add(g.key);
          matched.push({ id: g.key, ...g });
        }
      }
      i += consumed;
    }
  }

  return { matched, unmatched };
}

export function validateZipCode(str) {
  return typeof str === "string" && /^\d{5}$/.test(str);
}

export function parseZipCSV(text) {
  const codes = new Set();
  const lines = (text || "").split(/\r?\n/);
  for (const line of lines) {
    const cells = line.split(/[,;\t]/).map(c => c.trim()).filter(Boolean);
    for (const cell of cells) {
      if (validateZipCode(cell)) codes.add(cell);
    }
  }
  return Array.from(codes);
}

export function buildZipGeoObject(zip, clusivity = "include") {
  return { id: `ZIP:${zip}`, key: zip, name: zip, display_name: zip, type: "zip_code", clusivity };
}

// A measurement geo is targeted like any other but edited only by support and never shown to
// advertisers. It lives in the same campaign_settings.geo column, marked `measurement: true`.
export const isMeasurementGeo = g => g?.measurement === true;

// The marker lives only in the stored column: split strips it and merge writes it, so the
// two lists in memory are plain geos that can move between them. A stored snapshot can hold
// null or non-object entries (the CSM notification formats it raw), which pass through as they are.
const unmarked = g => {
  if (!g || typeof g !== "object") return g;
  const { measurement, ...rest } = g;
  return rest;
};

export function splitMeasurementGeos(geoList) {
  const geos = [];
  const measurementGeos = [];
  for (const g of geoList) (isMeasurementGeo(g) ? measurementGeos : geos).push(unmarked(g));
  return { geos, measurementGeos };
}

export function mergeMeasurementGeos(geos, measurementGeos) {
  return [...geos.map(unmarked), ...measurementGeos.map(g => ({ ...g, measurement: true }))];
}
